import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { loadPdfDocument, PdfDocument } from '@/core/utils/pdf-document';

interface Props {
    data: Uint8Array;
    onRendered?: () => void;
}

export const PdfDocumentViewer = ({ data, onRendered }: Props) => {
    const [document, setDocument] = useState<PdfDocument | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [zoom, setZoom] = useState(1);
    const [rendering, setRendering] = useState(true);
    const [error, setError] = useState(false);
    const canvas = useRef<HTMLCanvasElement>(null);
    const viewport = useRef<HTMLDivElement>(null);
    const firstPaint = useRef(false);
    const renderedCallback = useRef(onRendered);
    renderedCallback.current = onRendered;

    useEffect(() => {
        let disposed = false;
        let loaded: PdfDocument | undefined;
        setDocument(null); setPageNumber(1); setZoom(1); setRendering(true); setError(false);
        firstPaint.current = false;
        // PDF.js may transfer the input buffer to its worker; keep the original for downloads.
        loadPdfDocument(data.slice()).then(pdf => {
            loaded = pdf;
            if (disposed) { void pdf.destroy(); return; }
            setDocument(pdf);
        }).catch(() => { if (!disposed) { setError(true); setRendering(false); } });
        return () => { disposed = true; if (loaded) void loaded.destroy(); };
    }, [data]);

    useEffect(() => {
        if (!document) return;
        let disposed = false;
        let cancel: (() => void) | undefined;
        setRendering(true); setError(false);
        const renderPage = async () => {
            const page = await document.getPage(pageNumber);
            if (disposed || !canvas.current) return;
            const surface = canvas.current;
            const context = surface.getContext('2d');
            if (!context) throw new Error('Canvas unavailable');
            const natural = page.getViewport({ scale: 1 });
            const availableWidth = Math.max(240, (viewport.current?.clientWidth || 744) - 24);
            const scale = availableWidth / natural.width * zoom;
            const view = page.getViewport({ scale });
            const density = Math.min(window.devicePixelRatio || 1, 2);
            surface.width = Math.floor(view.width * density);
            surface.height = Math.floor(view.height * density);
            surface.style.width = `${Math.floor(view.width)}px`;
            surface.style.height = `${Math.floor(view.height)}px`;
            const task = page.render({
                canvas: surface, canvasContext: context, viewport: view,
                transform: density === 1 ? undefined : [density, 0, 0, density, 0, 0],
            });
            cancel = () => task.cancel();
            await task.promise;
            if (disposed) return;
            setRendering(false);
            if (!firstPaint.current) { firstPaint.current = true; renderedCallback.current?.(); }
        };
        renderPage().catch(() => { if (!disposed) { setError(true); setRendering(false); } });
        return () => { disposed = true; cancel?.(); };
    }, [document, pageNumber, zoom]);

    return <div>
        <div className="mb-3 flex flex-wrap items-center gap-2" role="toolbar" aria-label="Điều khiển PDF">
            <Button onClick={() => setPageNumber(n => n - 1)} disabled={!document || rendering || pageNumber <= 1}>Trang trước</Button>
            <span aria-live="polite">{document ? `Trang ${pageNumber} / ${document.numPages}` : 'Đang tải PDF…'}</span>
            <Button onClick={() => setPageNumber(n => n + 1)} disabled={!document || rendering || pageNumber >= document.numPages}>Trang sau</Button>
            <Button aria-label="Thu nhỏ PDF" onClick={() => setZoom(n => Math.max(0.5, n - 0.25))} disabled={!document || rendering || zoom <= 0.5}>−</Button>
            <span>{Math.round(zoom * 100)}%</span>
            <Button aria-label="Phóng to PDF" onClick={() => setZoom(n => Math.min(2, n + 0.25))} disabled={!document || rendering || zoom >= 2}>+</Button>
        </div>
        {error && <Alert type="error" showIcon message="Không thể hiển thị PDF" description="Vui lòng lấy lại PDF hoặc tải file để mở trên thiết bị." />}
        <div ref={viewport} className="w-full overflow-auto rounded border bg-gray-100 p-3" style={{ height: '65vh' }} aria-busy={rendering}>
            {rendering && <div className="flex justify-center gap-2 py-5" role="status"><Spin /><span>Đang hiển thị PDF…</span></div>}
            {!error && <canvas ref={canvas} role="img" aria-label={`Trang PDF ${pageNumber}`} style={{ display: rendering ? 'none' : 'block', margin: '0 auto', background: 'white' }} />}
        </div>
    </div>;
};
